import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import classnames from "classnames";
import styles from "./table-row.module.scss";
import sharedStyles from "../../Table.module.scss";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { getPrettyPhone } from "@whatslly/commons/utils";
import { Switch } from "../../../../BaseElements/Switch";
import { placement, Tooltip } from "../../../../../Tooltip";
import { TuvisSkeleton } from "../../../../../TuvisSkeleton";
import get from "lodash/get";
import type { TableColumn, HoverActionType } from "../../TableTypes";
import { OverflowCheckDiv } from "../../../../utilsComponents/OverflowCheckDiv";
import { SvgV2 } from "@whatslly/ui-components/v2/BaseElements/SvgV2";
import { ActionsMenu, ActionType } from "@whatslly/ui-components/ActionsMenu";
import { Checkbox } from "@whatslly/ui-components/v2/Checkbox";
dayjs.extend(localizedFormat);

const SKELETON_HEIGHT = "19px";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RowPropsType<T extends Record<string, any> = any> = {
  item: T;
  getLink?: (item: T) => string | undefined;
  linkTarget?: string;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: TableColumn<any>[];
  gridTemplateColumns?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>, item: T) => void;
  hoverActions?: HoverActionType<T>[];
  actionsMenuActions?: ActionType<T>[];
  rowClassName?: string;
  disableHoverActionsWrapperBackgroundColor?: boolean;
  onToggleRow?: (item: T) => void;
  isSelected?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
};

export const TableRow = observer(
  React.forwardRef<HTMLAnchorElement, RowPropsType>(
    (
      {
        item,
        getLink,
        linkTarget,
        loading,
        columns,
        gridTemplateColumns,
        onClick,
        hoverActions,
        actionsMenuActions,
        rowClassName,
        disableHoverActionsWrapperBackgroundColor,
        onToggleRow,
        isSelected,
        isExpandable,
        isExpanded,
        onToggleExpand,
      },
      ref
    ) => {
      const href = useMemo(() => getLink?.(item), [getLink, item]);
      return (
        <a
          data-testid={`table-row`}
          ref={ref}
          className={classnames(styles.row, { [styles.rowHoverDisabled]: !href }, rowClassName)}
          target={linkTarget ?? "_blank"}
          href={href}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => onClick?.(e, item)}
        >
          {isExpandable && (
            <div
              role="button"
              tabIndex={0}
              data-testid="table-row-expand"
              className={classnames(styles.expandChevron, { [styles.expandChevronOpen]: isExpanded })}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onToggleExpand?.();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  onToggleExpand?.();
                }
              }}
            >
              ▶
            </div>
          )}
          <div className={classnames(styles.tableGridWrapper, { [styles.tableGridWrapperExpandable]: isExpandable })}>
            <div
              className={classnames(sharedStyles.tableGrid, styles.tableGrid)}
              style={{ gridTemplateColumns: onToggleRow ? `0.1fr ${gridTemplateColumns}` : gridTemplateColumns }}
            >
              {onToggleRow && (
                <div
                  className={styles.checkboxWrapper}
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                  }}
                >
                  <Checkbox
                    testId="table-row-select-checkbox"
                    classNameChecked={styles.checkboxChecked}
                    className={styles.checkbox}
                    onChange={() => {
                      //ignore
                    }}
                    value={isSelected}
                    size={"large"}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      onToggleRow(item);
                    }}
                  />
                </div>
              )}
              {columns.map((column, index) => {
                const value = column.getValue ? column.getValue(item) : get(item, column.keyInsideItem, column.defaultValue);
                switch (column.type) {
                  case "switch":
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        <SwitchColumn
                          disabled={column.disabled?.(item) ?? false}
                          data-testid={column["data-testid"]}
                          checked={value}
                          onChange={column.onChange}
                          row={item}
                          className={column.className}
                        />
                      </TuvisSkeleton>
                    );
                  case "tags":
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        <TagsColumn
                          key={index}
                          tags={value}
                          maxVisible={column.maxVisibleTags}
                          data-testid={column["data-testid"]}
                          className={column.className}
                        />
                      </TuvisSkeleton>
                    );
                  case "tag":
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        <SingleTag
                          data-testid={column["data-testid"]}
                          text={value}
                          className={column.getClassName ? column.getClassName(item) : undefined}
                          centerTag={column.centerHeader}
                        />
                      </TuvisSkeleton>
                    );

                  case "date":
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        <DateColumn
                          data-testid={`user-date-test-${item.name}`}
                          date={value}
                          className={column.className}
                          tooltipDirection={column.tooltipDirection}
                        />
                      </TuvisSkeleton>
                    );
                  case "phone":
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        <PhoneColumn
                          data-testid={`user-phone-test-${item.name}`}
                          phone={value}
                          className={column.className}
                          tooltipDirection={column.tooltipDirection}
                        />
                      </TuvisSkeleton>
                    );
                  case "booleanIcons":
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        <BooleanIconsColumn
                          data-testid={`user-phone-test-${item.name}`}
                          isTrue={value}
                          className={column.className}
                          settings={column.booleanIconsSettings}
                          onChange={column.onChange}
                          disabled={column.disabled?.(item) ?? false}
                          row={item}
                        />
                      </TuvisSkeleton>
                    );
                  default:
                    return (
                      <TuvisSkeleton height={SKELETON_HEIGHT} loading={loading} key={index}>
                        {React.isValidElement(value) ? (
                          value
                        ) : (
                          <TextColumn
                            data-testid={column["data-testid"]}
                            value={value}
                            className={column.className}
                            row={item}
                            onClick={(column as any).onClick}
                            tooltipDirection={column.tooltipDirection}
                          />
                        )}
                      </TuvisSkeleton>
                    );
                }
              })}
              {hoverActions && !loading && (
                <HoverActions
                  hoverActions={hoverActions}
                  rowData={item}
                  disableHoverActionsWrapperBackgroundColor={disableHoverActionsWrapperBackgroundColor}
                />
              )}
              {actionsMenuActions && Boolean(actionsMenuActions?.length) && !loading && (
                <ActionsMenu className={styles.actionsMenu} actions={actionsMenuActions} item={item} testId="actions-menu" />
              )}
            </div>
          </div>
        </a>
      );
    }
  )
);

const DateColumn: React.FC<{ "data-testid"?: string; date: string | undefined; className?: string; tooltipDirection?: placement }> = ({
  date,
  className,
  tooltipDirection,
  ...props
}) => {
  const dateFormatted = useMemo(() => (date ? dayjs(date).format("L") : ""), [date]);

  return (
    <OverflowCheckDiv
      className={classnames(styles.rowCol, className)}
      tooltipDirection={tooltipDirection}
      data-testid={props["data-testid"]}
      tooltipText={dateFormatted}
    >
      {dateFormatted}
    </OverflowCheckDiv>
  );
};

const PhoneColumn: React.FC<{ "data-testid"?: string; phone: string | undefined; className?: string; tooltipDirection?: placement }> = ({
  phone,
  className,
  tooltipDirection,
  ...props
}) => {
  const phoneFormatted = useMemo(() => (phone ? getPrettyPhone(String(phone)) : ""), [phone]);
  return (
    <OverflowCheckDiv
      className={classnames(styles.rowCol, className)}
      data-testid={props["data-testid"]}
      tooltipText={phoneFormatted}
      tooltipDirection={tooltipDirection}
    >
      {phoneFormatted}
    </OverflowCheckDiv>
  );
};

const TagsColumn: React.FC<{
  "data-testid"?: string;
  tags: { label: string; description?: string }[];
  maxVisible?: number;
  className?: string;
}> = ({ tags, maxVisible, className, ...props }) => {
  const testId = props["data-testid"] || "tag";
  const collapse = maxVisible !== undefined && tags?.length > maxVisible;
  const visible = collapse ? tags.slice(0, maxVisible) : tags;
  const hidden = collapse ? tags.slice(maxVisible) : [];
  const overflowTag = collapse ? { label: `+${hidden.length}`, description: hidden.map((tag) => tag.label).join(", ") } : undefined;

  return (
    <div
      className={classnames(styles.rowCol, styles.tagsColumn, className, { [styles.tagsColumnSingleRow]: collapse })}
      data-testid={props["data-testid"]}
    >
      {visible?.map((tag, index) => {
        return <Tag key={index} tag={tag} data-testid={`${testId}-${index}`} />;
      })}
      {overflowTag && <Tag tag={overflowTag} data-testid={`${testId}-overflow`} />}
    </div>
  );
};

const Tag: React.FC<{ "data-testid"?: string; tag: { label: string; description?: string } }> = ({ tag, ...props }) => {
  return (
    <Tooltip value={tag.description} disabled={!tag.description} data-testid={`${props["data-testid"]}-tooltip`}>
      <div className={classnames(styles.tag, styles.tagContainer)} data-testid={props["data-testid"]}>
        {tag.label}
      </div>
    </Tooltip>
  );
};

const SingleTag: React.FC<{
  "data-testid"?: string;
  className?: string;
  text?: string;
  centerTag?: boolean;
}> = ({ text, className, centerTag, ...props }) => {
  if (!text) return <></>;

  return (
    <div className={classnames({ [styles.centerSingleTag]: centerTag })}>
      <div className={classnames(styles.singleTag, styles.singleTagContainer, className)} data-testid={props["data-testid"]}>
        {text}
      </div>
    </div>
  );
};

const SwitchColumn: React.FC<{
  "data-testid"?: string;
  checked: boolean;
  className?: string;
  row: any;
  disabled: boolean;
  onChange?: (item: any) => void;
}> = ({ checked, onChange, disabled, className, row, ...props }) => {
  return (
    <div className={classnames(styles.rowCol, styles.switchCol, className)}>
      <Switch
        testId={props["data-testid"]}
        checked={checked}
        onChange={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onChange?.(row);
        }}
        disabled={disabled}
      />
    </div>
  );
};

const TextColumn: React.FC<{
  "data-testid"?: string;
  value: string;
  row: Record<string, any>;
  className?: string;
  onClick?: (column: Record<string, any>) => void;
  tooltipDirection?: placement;
}> = ({ value, className, tooltipDirection, ...props }) => {
  return (
    <OverflowCheckDiv
      className={classnames(styles.rowCol, { [styles.clickAble]: props.onClick }, className)}
      data-testid={props["data-testid"]}
      tooltipText={value}
      tooltipDirection={tooltipDirection}
      onClick={() => props.row && props.onClick?.(props.row)}
    >
      {value}
    </OverflowCheckDiv>
  );
};

const BooleanIconsColumn: React.FC<{
  "data-testid"?: string;
  isTrue: boolean;
  className?: string;
  settings: { trueIcon: React.ReactNode; falseIcon: React.ReactNode };
  row: any;
  disabled: boolean;
  onChange?: (item: any) => void;
}> = ({ isTrue, onChange, disabled, className, row, settings, ...props }) => {
  return (
    <div
      className={classnames(styles.rowCol, styles.booleanIconsColumn, className, { [styles.disabled]: disabled })}
      onClick={(e) => {
        if (disabled) return;
        e.stopPropagation();
        e.preventDefault();
        onChange?.(row);
      }}
      {...props}
    >
      {isTrue ? settings.trueIcon : settings.falseIcon}
    </div>
  );
};

const HoverActions: React.FC<{ hoverActions: HoverActionType[]; rowData: unknown; disableHoverActionsWrapperBackgroundColor?: boolean }> = React.memo(
  ({ hoverActions, rowData, disableHoverActionsWrapperBackgroundColor }) => {
    return (
      <div className={classnames(styles.hoverActionsWrapper, { [styles.unsetBg]: disableHoverActionsWrapperBackgroundColor })}>
        {hoverActions?.map((hoverAction) => (
          <React.Fragment key={hoverAction.tooltip}>
            <HoverAction hoverAction={hoverAction} rowData={rowData} />
          </React.Fragment>
        ))}
      </div>
    );
  }
);

const HoverAction: React.FC<{ hoverAction: HoverActionType; rowData: unknown }> = ({ hoverAction, rowData }) => {
  const { icon: Icon, tooltip, onClick } = hoverAction;

  const iconToUse = useMemo(() => {
    if (typeof Icon === "string") return <SvgV2 iconName={Icon} />;
    return <Icon />;
  }, [Icon]);

  if (hoverAction.hide?.(rowData)) {
    return null;
  }

  return (
    <Tooltip value={<>{tooltip}</>}>
      {hoverAction.getHref ? (
        <a target={"_self"} className={classnames(styles.hoverAction, hoverAction.className)} href={hoverAction.getHref(rowData)}>
          {iconToUse}
        </a>
      ) : (
        <div
          className={classnames(styles.hoverAction, hoverAction.className)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick?.(rowData);
          }}
        >
          {iconToUse}
        </div>
      )}
    </Tooltip>
  );
};
